import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React, {useCallback} from 'react';
import {useParams} from 'react-router';
import styled, {css} from 'styled-components';

import {trackObservabilityIssueAlertClick} from '../../services/analytics/observabilityEvents';
import {type LegendPosition} from '../../util/plotHelpers/legendTypes';
import {InstrumentedWaveLoader} from '../utility/InstrumentedLoader';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {
  useActiveIssues,
  useAnnotationActive,
  useHandleIssueInteraction,
  useHoveredIssue,
  useIssueRampStatus,
  useSetSelectedIssue,
} from './../../state/issues/hooks';
import {useIconPositioningContext} from './../../state/issues/IconPositioningContext';
import {DelayRender} from './DelayRender';
import {usePanelInteractionContext} from './PanelInteractionContext';
import {usePanelSystemMetricsContext} from './SystemMetrics/PanelSystemMetricsContext';
import {usePanelZoom2} from './zoom/PanelZoomContext2';

type PlotLayoutProps = {
  allowIssuesAlerts?: boolean;
  fontSize: number;
  Graph: React.ReactNode;
  handleHover: (isHovered: boolean) => void;
  Legend: React.ReactNode;
  legendPosition: LegendPosition;
  loading: boolean;
  numLines: number;
  showLegend: boolean;
  titleText: string;
  Title: React.ReactNode;
  Warning: React.ReactNode;
};

const Wrapper = styled.div<
  Pick<PlotLayoutProps, 'legendPosition' | 'showLegend'> & {
    delayRender?: boolean;
    issueStatus: 'enabled' | 'disabled';
    legendHeight: number;
  }
>`
  display: grid;
  height: 100%;
  column-gap: 12px;
  row-gap: 2px;
  position: relative;
  ${({delayRender, issueStatus, legendHeight, legendPosition, showLegend}) => {
    return (
      getArea({
        delayRender,
        issueStatus,
        legendHeight,
        legendPosition,
        showLegend,
      }) ?? ''
    );
  }}
`;

const TitleWrapper = styled.div`
  grid-area: title;
`;

const WarningWrapper = styled.div`
  grid-area: warning;
`;

const IssueWrapper = styled.div`
  grid-area: issues;
`;

const GraphWrapper = styled.div`
  grid-area: graph;
  cursor: crosshair;
  height: 100%;
  position: relative;
  width: 100%;
`;

const LegendWrapper = styled.div<Pick<PlotLayoutProps, 'legendPosition'>>`
  grid-area: legend;
  overflow-y: auto;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${({legendPosition}) => {
    switch (legendPosition) {
      case 'north':
      case 'south': {
        return `
          display: flex;
          flex-wrap: wrap;
        `;
      }
      default:
        return ``;
    }
  }}
`;

const AreaLayouts = (
  key: string,
  legendHeight: number
): ReturnType<typeof css> | undefined =>
  ({
    BaseAreaDelayRender: css`
      grid-template-areas:
        'title'
        'delay-render';
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr;
    `,
    BaseAreaNoIssuesNoLegend: css`
      grid-template-areas:
        'title'
        'warning'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 1fr;
    `,
    BaseAreaIssuesNoLegend: css`
      grid-template-areas:
        'title'
        'warning'
        'issues'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 20px 1fr;
    `,
    BaseAreaNoIssuesLegend_north: css`
      grid-template-areas:
        'title'
        'warning'
        'legend'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) fit-content(
          ${legendHeight}px
        ) 1fr;
    `,
    BaseAreaNoIssuesLegend_south: css`
      grid-template-areas:
        'title'
        'warning'
        'graph'
        'legend';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 1fr fit-content(
          ${legendHeight}px
        );
    `,
    BaseAreaNoIssuesLegend_west: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'legend graph';
      grid-template-columns: minmax(auto, 3fr) 7fr;
      grid-template-rows: auto fit-content(14px) 1fr;
    `,
    BaseAreaNoIssuesLegend_east: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'graph legend';

      grid-template-columns: 7fr minmax(auto, 3fr);
      grid-template-rows: auto fit-content(14px) 1fr;
    `,
    BaseAreaIssuesLegend_north: css`
      grid-template-areas:
        'title'
        'warning'
        'issues'
        'legend'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows:
        auto fit-content(14px) 20px fit-content(${legendHeight}px)
        1fr;
    `,
    BaseAreaIssuesLegend_south: css`
      grid-template-areas:
        'title'
        'warning'
        'issues'
        'graph'
        'legend';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 20px 1fr fit-content(
          ${legendHeight}px
        );
    `,
    BaseAreaIssuesLegend_west: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'issues issues'
        'legend graph';
      grid-template-columns: minmax(auto, 3fr) 7fr;
      grid-template-rows: auto fit-content(14px) 20px 1fr;
    `,
    BaseAreaIssuesLegend_east: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'issues issues'
        'graph legend';
      grid-template-columns: 7fr minmax(auto, 3fr);
      grid-template-rows: auto fit-content(14px) 20px 1fr;
    `,
  })[key];

const getArea = ({
  delayRender,
  issueStatus,
  legendHeight,
  legendPosition,
  showLegend,
}: {
  delayRender?: boolean;
  issueStatus: 'enabled' | 'disabled';
  legendHeight: number;
  legendPosition: LegendPosition;
  showLegend: boolean;
}) => {
  if (delayRender) {
    return AreaLayouts('BaseAreaDelayRender', legendHeight);
  }
  const legend = showLegend ? `Legend_${legendPosition}` : 'NoLegend';
  const issues = issueStatus === 'enabled' ? 'Issues' : 'NoIssues';
  const base = `BaseArea${issues}${legend}`;

  return AreaLayouts(base, legendHeight);
};

const getIssuesWrapperStatus = ({
  annotationActive,
  issueCount,
  issueRampStatus,
}: {
  annotationActive: boolean;
  issueCount: number;
  issueRampStatus: 'enabled' | 'disabled';
}) => {
  const failConditions = [
    !annotationActive,
    issueCount === 0,
    issueRampStatus === 'disabled',
  ];

  return failConditions.some(Boolean) ? 'disabled' : 'enabled';
};

export const PlotLayout = ({
  fontSize,
  Graph,
  handleHover,
  Legend,
  legendPosition,
  loading,
  numLines,
  showLegend,
  titleText,
  Title,
  Warning,
  ...props
}: PlotLayoutProps) => {
  'use memo';
  const {sizeRef} = usePanelInteractionContext();

  const annotationActive = useAnnotationActive();
  const activeIssues = useActiveIssues();
  const hoveredIssue = useHoveredIssue();
  const issueRampStatus = useIssueRampStatus();
  const {queryZoom} = usePanelZoom2();
  const {
    hasAfterIssues,
    hasBeforeIssues,
    sortedPositionedIssues,
    marginRight,
    marginLeft,
  } = useIconPositioningContext();
  const {isDelayedSystemMetrics} = usePanelSystemMetricsContext();

  const issuesStatus = getIssuesWrapperStatus({
    annotationActive,
    issueCount: (activeIssues ?? []).length,
    issueRampStatus,
  });

  // Bound to both `mouseenter` and `mousemove`. `mouseenter` alone only fires
  // when the pointer crosses the boundary, so a panel that mounts under an
  // already-stationary pointer — opening the panel settings drawer while
  // hovering the chart — never registers as hovered, and LinePlotCrosshair
  // renders its non-hovered (truncated) tooltip copy instead of the real one.
  // React bails out of the re-render when the state is unchanged, so the repeat
  // calls on subsequent moves cost a function call and nothing more.
  const handleHoverStart = useCallback(() => handleHover(true), [handleHover]);

  const handleMouseLeave = useCallback(() => handleHover(false), [handleHover]);

  return (
    <TailwindContents>
      <Wrapper
        data-test="runs-line-plot-layout"
        delayRender={isDelayedSystemMetrics}
        issueStatus={issuesStatus}
        key="plot-layout"
        legendHeight={fontSize * 4 + 8}
        legendPosition={legendPosition}
        onMouseEnter={handleHoverStart}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleHoverStart}
        showLegend={showLegend}
        ref={sizeRef}
        {...props}>
        <TitleWrapper
          className="overflow-hidden"
          data-testid="title-wrapper"
          data-dd-action-name="Line plot title">
          {Title}
        </TitleWrapper>
        {isDelayedSystemMetrics ? (
          <DelayRender title={titleText ?? ''} />
        ) : (
          <>
            <WarningWrapper
              className="flex justify-center overflow-hidden"
              data-testid="warning-wrapper">
              {Warning}
            </WarningWrapper>
            {issuesStatus === 'enabled' && (
              <IssueWrapper
                className="relative flex items-center overflow-hidden"
                data-testid="issue-alerts">
                <div className="h-20">
                  {hasBeforeIssues && queryZoom.xAxisMin == null && (
                    <IssueAlert
                      key={`issue-alert-before`}
                      isActive={false}
                      left={marginLeft - 6}
                      id={'before'}
                      index={sortedPositionedIssues.length * -1}>
                      &#x2190;
                    </IssueAlert>
                  )}
                  {annotationActive &&
                    sortedPositionedIssues.length > 0 &&
                    sortedPositionedIssues.map(([id, left], index) => (
                      <IssueAlert
                        // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                        key={`issue-alert-${index}`}
                        isActive={hoveredIssue === id}
                        left={left - 6}
                        id={id}
                        index={index}>
                        !
                      </IssueAlert>
                    ))}
                  {hasAfterIssues && queryZoom.xAxisMax == null && (
                    <IssueAlert
                      key={`issue-alert-final`}
                      isActive={false}
                      right={marginRight - 6}
                      id={'final'}
                      index={sortedPositionedIssues.length + 1}>
                      &#x2192;
                    </IssueAlert>
                  )}
                </div>
              </IssueWrapper>
            )}
            <GraphWrapper
              data-test="graph-wrapper"
              data-dd-action-name="Line plot">
              {Graph}
            </GraphWrapper>
            {showLegend && (
              <LegendWrapper legendPosition={legendPosition}>
                {Legend}
              </LegendWrapper>
            )}
            {loading && (
              <>
                {numLines > 0 ? (
                  <div className="absolute z-10">
                    <InstrumentedWaveLoader
                      size="small"
                      name="panel-layout-loading-small"
                      // Delay showing the loading indicator by 50ms to prevent flickering.
                      // When data is cached, it often loads within 50ms, so showing the loader
                      // immediately would result in a brief flash. This delay ensures we only
                      // show the loader for meaningful loading states.
                      delayBeforeShow={50}
                    />
                  </div>
                ) : (
                  <div className="absolute flex h-full w-full items-center justify-center ">
                    <InstrumentedWaveLoader
                      name="panel-layout-loading-huge"
                      size="huge"
                      // Delay showing the loading indicator by 50ms to prevent flickering.
                      // When data is cached, it often loads within 50ms, so showing the loader
                      // immediately would result in a brief flash. This delay ensures we only
                      // show the loader for meaningful loading states.
                      delayBeforeShow={50}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Wrapper>
    </TailwindContents>
  );
};

const IssueAlert = ({
  children,
  index,
  isActive,
  id,
  left,
  right,
}: {
  children: React.ReactNode;
  left?: number;
  right?: number;
  isActive: boolean;
  id: string;
  index: number;
}) => {
  'use memo';
  const handleIssueInteraction = useHandleIssueInteraction();
  const setSelectedIssue = useSetSelectedIssue();
  const {contentType, toggleWorkspaceDrawer} = useWorkspaceDrawerContext();
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const classes = clsx({
    'text-red-350': !isActive,
    'text-white': isActive,
    'bg-red-500': isActive,
    'bg-white': !isActive,
    'border-red-350': !isActive,
    'border-red-500': isActive,
  });

  const style: React.CSSProperties = {
    bottom: 0,
    zIndex: index,
  };
  if (typeof left === 'number') {
    style.left = left;
  }
  if (typeof right === 'number') {
    style.right = right;
  }
  return (
    <button
      style={style}
      key={`issue-alert-${index}`}
      className={`${classes} absolute flex h-12 w-12 cursor-pointer items-center justify-center rounded-sm border-[1px] text-[8px] transition-all duration-150 hover:bg-red-500 hover:text-white focus-visible:outline-0`}
      onClick={() => {
        trackObservabilityIssueAlertClick({
          action: 'Click issue alert',
          entityName: entityName ?? '',
          projectName: projectName ?? '',
        });
        if (contentType !== 'run-issues') {
          toggleWorkspaceDrawer('run-issues');
        }
        setSelectedIssue(id);
      }}
      onMouseEnter={handleIssueInteraction('hover', id)}
      onMouseLeave={handleIssueInteraction('unhover', null)}>
      {children}
    </button>
  );
};
